import { Table, Button, Tag, Space, Popconfirm, App } from 'antd'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { deleteAnnouncement } from '../services/announcement-api'
import { bannerTypeLabels, type Announcement, type BannerType } from '../types/announcement.types'
import type { ColumnsType } from 'antd/es/table'
import './AnnouncementsTable.css'

const bannerTypeColors: Record<BannerType, string> = {
  info: 'blue',
  success: 'green',
  warning: 'orange',
  error: 'red'
}

interface AnnouncementsTableProps {
  announcements: Announcement[]
  loading: boolean
  showInactive: boolean
  onEdit: (announcement: Announcement) => void
  onRefresh: () => void
}

export function AnnouncementsTable({
  announcements,
  loading,
  showInactive,
  onEdit,
  onRefresh
}: AnnouncementsTableProps) {
  const { message } = App.useApp()

  const handleDelete = async (id: string) => {
    try {
      await deleteAnnouncement(id)
      message.success('Announcement deleted')
      onRefresh()
    } catch (error: any) {
      console.error('Error deleting announcement:', error)
      message.error(error.response?.data?.message || 'Failed to delete announcement')
    }
  }

  const getStatus = (announcement: Announcement) => {
    if (!announcement.isActive) {
      return { label: 'Inactive', color: 'default' }
    }
    const now = dayjs()
    const start = dayjs(announcement.startDate)
    const end = dayjs(announcement.endDate)

    if (now.isBefore(start)) {
      return { label: 'Scheduled', color: 'blue' }
    }
    if (now.isAfter(end)) {
      return { label: 'Expired', color: 'orange' }
    }
    return { label: 'Active', color: 'green' }
  }

  const columns: ColumnsType<Announcement> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      sorter: (a, b) => a.code.localeCompare(b.code)
    },
    {
      title: 'Type',
      dataIndex: 'bannerType',
      key: 'bannerType',
      width: 70,
      sorter: (a, b) => a.bannerType.localeCompare(b.bannerType),
      render: (type: BannerType) => (
        <Tag color={bannerTypeColors[type]} style={{ fontSize: '11px', padding: '0 7px' }}>
          {bannerTypeLabels[type]}
        </Tag>
      )
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      width: 300,
      sorter: (a, b) => a.message.localeCompare(b.message)
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 80,
      sorter: (a, b) => (a.tenantCode || '').localeCompare(b.tenantCode || ''),
      render: (_, record) =>
        record.tenantCode ? (
          <Tag style={{ fontSize: '11px', padding: '0 7px' }}>{record.tenantCode}</Tag>
        ) : (
          <Tag color="purple" style={{ fontSize: '11px', padding: '0 7px' }}>
            Global
          </Tag>
        )
    },
    {
      title: 'Start',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
      sorter: (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: 'End',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 120,
      sorter: (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    ...(showInactive
      ? [
          {
            title: 'Status',
            key: 'status',
            width: 100,
            sorter: (a: Announcement, b: Announcement) => {
              const aStatus = getStatus(a).label
              const bStatus = getStatus(b).label
              return aStatus.localeCompare(bStatus)
            },
            render: (_: any, record: Announcement) => {
              const status = getStatus(record)
              return (
                <Tag color={status.color} style={{ fontSize: '11px', padding: '0 7px' }}>
                  {status.label}
                </Tag>
              )
            }
          }
        ]
      : []),
    {
      title: 'Order',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 50,
      align: 'center',
      sorter: (a, b) => a.displayOrder - b.displayOrder
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          <Popconfirm
            title="Delete announcement"
            description="Are you sure you want to delete this announcement?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div className="announcements-table-wrapper" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Table
        size="small"
        columns={columns}
        dataSource={announcements}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content', y: 'calc(100vh - 200px)' }}
        pagination={false}
        style={{ flex: 1 }}
      />
    </div>
  )
}
